import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Heading } from '../../components';
import Icons from '../../components/Icons/Icons';
import ExtraIcon from './ExtraIcon';
import { sanitizeAddons } from '../../util/addonCategories';

import css from './ListingPage.module.css';

/**
 * SectionOptionalExtras — renter-facing display of the extras an owner offers.
 *
 * Reads the existing `publicData.addons` ([{ id, name, price }]) — the same data that
 * already powers the paid booking add-on selector (OrderPanel/RenderAddons). Here we just
 * SURFACE them as a browse-able spec-card grid on the listing page (icon + name + price),
 * matching the Trailer Details grid so it feels native. FREE for all listings.
 *
 * Owners type add-on names freely, so we run them through `sanitizeAddons`, which drops
 * junk entries (owner names, bare numbers) and attaches a category (icon) to each survivor.
 */
const formatPrice = price => {
  const n = parseFloat(price);
  return Number.isFinite(n) && n > 0 ? `$${n.toFixed(2)}` : null;
};

const SectionOptionalExtras = props => {
  const { publicData } = props;
  const extras = sanitizeAddons(publicData?.addons);

  if (!extras.length) {
    return null;
  }

  return (
    <section className={css.sectionOptionalExtras}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        <Icons name="checkMark" />
        <FormattedMessage id="ListingPage.optionalExtrasHeading" defaultMessage="Optional Extras" />
      </Heading>
      <p className={css.optionalExtrasIntro}>
        <FormattedMessage
          id="ListingPage.optionalExtrasIntro"
          defaultMessage="Add-ons this owner offers. Ask the owner about availability when you reach out."
        />
      </p>
      <dl className={css.specGrid}>
        {extras.map((extra, i) => {
          const priceLabel = formatPrice(extra.price);
          return (
            <div
              className={css.specCard}
              key={extra.id || `${extra.name}-${i}`}
              style={{ '--i': i }}
            >
              <span className={css.specIcon} aria-hidden="true">
                <ExtraIcon category={extra.category} />
              </span>
              <div className={css.specText}>
                {priceLabel ? <dt className={css.specLabel}>{priceLabel}</dt> : null}
                <dd className={css.specValue}>{extra.name}</dd>
              </div>
            </div>
          );
        })}
      </dl>
    </section>
  );
};

export default SectionOptionalExtras;
